/**
 * Iter 189 — CartDrawer: right-side sliding cart drawer.
 * ============================================================================
 *
 * Trigger it by rendering:
 *   <CartDrawerTrigger />   // a cart-icon button with item-count badge
 *
 * And placing anywhere in the tree:
 *   <CartDrawer />         // the actual drawer (portal-style overlay)
 *
 * Both read from CartContext. The drawer's "Checkout" button navigates to
 * `/checkout` (relative to the current site path).
 */
import React, { useState, createContext, useContext, useCallback } from 'react';
import { ShoppingBag, X, Plus, Minus, Trash2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';

const DrawerCtx = createContext({ open: false, setOpen: () => {} });

export function CartDrawerProvider({ children }) {
  const [open, setOpen] = useState(false);
  const value = { open, setOpen };
  return <DrawerCtx.Provider value={value}>{children}</DrawerCtx.Provider>;
}

export function useCartDrawer() {
  return useContext(DrawerCtx);
}


export function CartDrawerTrigger({ className = '', color = '#0E0E23' }) {
  const { setOpen } = useCartDrawer();
  const { itemCount } = useCart();
  return (
    <button
      onClick={() => setOpen(true)}
      className={`relative w-9 h-9 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity ${className}`}
      style={{ color }}
      data-testid="cart-drawer-trigger"
      aria-label={`Cart · ${itemCount} items`}
    >
      <ShoppingBag size={17} />
      {itemCount > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-[#A78BFA] text-white"
          data-testid="cart-drawer-count"
        >{itemCount}</span>
      )}
    </button>
  );
}


export function CartDrawer({ onCheckout }) {
  const { open, setOpen } = useCartDrawer();
  const { items, total, itemCount, updateQuantity, removeItem, clearCart } = useCart();

  const doCheckout = useCallback(() => {
    setOpen(false);
    if (onCheckout) { onCheckout(); return; }
    // Preview: /site/<subdomain>/... → go to /site/<subdomain>/checkout
    // Custom domain: hostname is the site → go to /checkout
    const m = window.location.pathname.match(/^\/site\/([^/]+)/);
    window.location.href = m ? `/site/${m[1]}/checkout` : '/checkout';
  }, [onCheckout, setOpen]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex" data-testid="cart-drawer">
      {/* Scrim — deeper black + subtle blur for atmospheric focus */}
      <div
        className="flex-1"
        style={{ background: 'rgba(7, 7, 26, 0.65)', backdropFilter: 'blur(6px)' }}
        onClick={() => setOpen(false)}
        data-testid="cart-drawer-scrim"
      />

      {/* Drawer surface — matches checkout aesthetic */}
      <div
        className="w-[460px] max-w-[92vw] relative flex flex-col text-white overflow-hidden"
        style={{
          background: '#07071A',
          boxShadow: '-30px 0 80px rgba(0,0,0,0.6)',
        }}
      >
        {/* Ambient glow — twin violet radials */}
        <div className="pointer-events-none absolute inset-0 opacity-80" aria-hidden="true">
          <div className="absolute -top-40 -right-40 w-[420px] h-[420px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.28) 0%, rgba(139,92,246,0) 65%)' }} />
          <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.20) 0%, rgba(236,72,153,0) 65%)' }} />
        </div>

        {/* Header */}
        <div className="relative px-6 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(167,139,250,0.15)' }}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#A78BFA]">Your Cart</p>
            <h3 className="text-2xl font-bold mt-1" style={{ fontFamily: 'Syne, sans-serif' }}>
              {itemCount} item{itemCount === 1 ? '' : 's'}
            </h3>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="cart-drawer-close"
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="relative flex-1 overflow-y-auto px-6 py-5">
          {items.length === 0 ? (
            <div className="py-20 text-center" data-testid="cart-drawer-empty">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.15), rgba(236,72,153,0.10))', border: '1px solid rgba(167,139,250,0.25)' }}
              >
                <ShoppingBag size={26} className="text-[#A78BFA]" />
              </div>
              <p className="text-base font-semibold text-white/90">Your cart is empty</p>
              <p className="text-xs text-white/45 mt-2">Start adding pieces you love — we&rsquo;ll hold them here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it, i) => (
                <div
                  key={`${it.product_id}-${it.size || ''}-${i}`}
                  className="flex gap-4 p-3 rounded-xl"
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                    border: '1px solid rgba(167,139,250,0.15)',
                  }}
                  data-testid={`cart-item-${it.product_id}`}
                >
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                  ) : (
                    <div
                      className="w-16 h-16 rounded-lg flex-shrink-0 flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
                    >
                      <ShoppingBag size={16} className="text-white/80" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate text-white">{it.name}</p>
                    {it.size && <p className="text-[11px] text-white/50 mt-0.5">Size: {it.size}</p>}
                    <div className="flex items-center gap-2 mt-2.5">
                      <button
                        onClick={() => updateQuantity(it.product_id, it.size, it.quantity - 1)}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                        data-testid={`cart-item-minus-${it.product_id}`}
                        aria-label="Decrease quantity"
                      >
                        <Minus size={11} />
                      </button>
                      <span className="text-sm w-6 text-center font-semibold">{it.quantity}</span>
                      <button
                        onClick={() => updateQuantity(it.product_id, it.size, it.quantity + 1)}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                        data-testid={`cart-item-plus-${it.product_id}`}
                        aria-label="Increase quantity"
                      >
                        <Plus size={11} />
                      </button>
                      <button
                        onClick={() => removeItem(it.product_id, it.size)}
                        className="ml-auto w-6 h-6 text-white/40 hover:text-red-400 transition-colors"
                        data-testid={`cart-item-remove-${it.product_id}`}
                        aria-label="Remove item"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-bold shrink-0 text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                    ${(it.price * it.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
              <button
                onClick={clearCart}
                className="text-[11px] text-white/40 hover:text-red-300 uppercase tracking-[0.18em] font-semibold transition-colors pt-2"
                data-testid="cart-drawer-clear"
              >
                Clear cart
              </button>
            </div>
          )}
        </div>

        {/* Footer summary + CTA */}
        {items.length > 0 && (
          <div
            className="relative px-6 py-5 space-y-4"
            style={{
              borderTop: '1px solid rgba(167,139,250,0.20)',
              background: 'linear-gradient(180deg, rgba(20,15,45,0.4) 0%, rgba(14,14,35,0.85) 100%)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">Subtotal</p>
                <p className="text-[11px] text-white/45 mt-1">Shipping + tax at checkout</p>
              </div>
              <span
                className="text-3xl font-bold"
                style={{ fontFamily: 'Syne, sans-serif' }}
                data-testid="cart-drawer-subtotal"
              >
                ${total.toFixed(2)}
              </span>
            </div>

            <button
              onClick={doCheckout}
              className="w-full py-3.5 rounded-full text-sm font-bold uppercase tracking-[0.18em] text-white transition-all hover:shadow-[0_10px_40px_rgba(167,139,250,0.55)] inline-flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 50%, #EC4899 100%)' }}
              data-testid="cart-drawer-checkout"
            >
              Checkout &middot; ${total.toFixed(2)}
            </button>

            <div className="flex items-center gap-2 text-[10px] text-white/40 justify-center">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Zero platform fees &middot; 100% to the maker
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
